import { CreateBundleRequest } from '../../redux/api/bundles';
import { BundleTube } from '../../models/bundle';
import { max } from 'lodash';
import { BundleMessages } from 'src/intl/BundleMessages';

/**
 * Trims the blank rows from the top and bottom of a bundle.
 * @param bundleGrid - The raw bundle grid with possible row padding.
 */
export const trimBundleGrid = (bundleGrid: string[][]): string[][] => {
  if (!bundleGrid || bundleGrid.length === 0) {
    return [[]];
  }

  const bundleRowLength = bundleGrid.length;

  let topIndex = 0;
  for (let t = 0; t < bundleRowLength; t++) {
    if (bundleGrid[t].every(val => !val?.trim())) {
      continue;
    }

    topIndex = t;
    break;
  }

  let bottomIndex = 0;
  // Iterate from "bottom" of grid until we find a row with a value (tube)
  for (let b = bundleRowLength - 1; b >= 0; b--) {
    if (bundleGrid[b].every(val => !val?.trim())) {
      continue;
    }

    bottomIndex = b;
    break;
  }
  return bundleGrid.slice(topIndex, bottomIndex + 1);
};

/**
 * Finds the "root" tube for Sentinel measurements. The root should
 * be the top-most and left-most tube based on the translations.
 */
export const getRootTube = (bundleTubes: BundleTube[]): BundleTube => {
  const maxY = max(bundleTubes.map(t => t.yCoordinate));
  const maxYTubes = bundleTubes.filter(t => t.yCoordinate === maxY);
  const minX = Math.min(...maxYTubes.map(t => t.xCoordinate));
  const rootTube = maxYTubes.find(t => t.xCoordinate === minX);
  if (!rootTube) {
    throw new Error('Error finding the root tube of the bundle.');
  }
  return rootTube;
};

export const parseBundleDetails = (
  detailsSection: string[][],
  formatMessage: (message: { id: string }) => string,
):
  | { success: false; request?: CreateBundleRequest; error: string }
  | { success: true; request: CreateBundleRequest; error: undefined } => {
  const pattern = detailsSection[5][4]?.toLowerCase();
  if (!isValidBundlePattern(pattern)) {
    return { success: false, error: formatMessage(BundleMessages.bundlePatternInvalid) };
  }

  const rotated = detailsSection[6][4]?.toLowerCase();
  if (!rotated) {
    return { success: false, error: formatMessage(BundleMessages.bundleRotationMissing) };
  }
  if (rotated !== 'yes' && rotated !== 'no') {
    return { success: false, error: formatMessage(BundleMessages.bundleRotationInvalid) };
  }

  const name = detailsSection[2][4];
  const site = detailsSection[3][4];
  const hxModelNumber = detailsSection[4][4];

  if (!name || !site || !hxModelNumber) {
    const missingItems = [];

    if (!name) missingItems.push(formatMessage(BundleMessages.missingItemsName));
    if (!site) missingItems.push(formatMessage(BundleMessages.missingItemsSite));
    if (!hxModelNumber) missingItems.push(formatMessage(BundleMessages.missingItemsHxModelNumber));

    if (missingItems.length > 0) {
      return {
        success: false,
        error: `${formatMessage(BundleMessages.missingRequiredFields)} ${missingItems.join(', ')}`,
      };
    }
  }

  const tubeLength = { value: detailsSection[10][4], unit: detailsSection[10][12] };
  if (!!tubeLength.value && tubeLength.value !== '0' && !tubeLength.unit) {
    return {
      success: false,
      error: formatMessage(BundleMessages.unitRequiredWithTubeLength),
    };
  }

  const pitch = { value: detailsSection[12][4], unit: detailsSection[12][12] };
  if (!!pitch.value && pitch.value !== '0' && !pitch.unit) {
    return {
      success: false,
      error: formatMessage(BundleMessages.unitRequiredWithPitch),
    };
  }

  const tubeDiameter = { value: detailsSection[11][4], unit: detailsSection[11][12] };
  if (!!tubeDiameter.value && tubeDiameter.value !== '0' && !tubeDiameter.unit) {
    return {
      success: false,
      error: formatMessage(BundleMessages.unitRequiredWithTubeDiameter),
    };
  }

  const request: CreateBundleRequest = {
    assetNumber: detailsSection[8][4],
    name,
    description: detailsSection[13][4],
    pattern,
    heatExchangerModelNumber: hxModelNumber,
    pitch: pitch.value,
    pitchUnit: pitch.unit,
    siteName: site,
    tubeCount: detailsSection[9][4],
    tubeDiameter: tubeDiameter.value,
    tubeDiameterUnit: tubeDiameter.unit,
    tubeLength: tubeLength.value,
    tubeLengthUnit: tubeLength.unit,
    rotated: rotated === 'yes',
    permissions: undefined,
    tubes: [],
  };

  return { success: true, request, error: undefined };
};

export const isValidBundlePattern = (pattern: unknown): pattern is 'square' | 'triangle' => {
  return pattern === 'square' || pattern === 'triangle';
};
